import React from "react";
import Textarea from "@/components/ui/Textarea";
import Button from "@/components/ui/Button";
import Textinput from "@/components/ui/Textinput";
import Tooltip from "@/components/Tooltip";
import Select from "@/components/ui/Select";
import Slider from "rc-slider";
import { PlanProBadge } from "@/components/partials/planBadges";
import { instructionsTemplates } from "@/pages/chatbot/settings/InstructionTemplates";
import { useTranslation } from "react-i18next";
import { ModelModal } from "@/pages/chatbot/ai_settings/ModelModal.tsx";
import { Model } from "@/pages/chatbot/ai_settings/ModelList.tsx";
import Loading from "@/components/Loading";
import { ChatSettings } from "@/models/chatbot.ts";
import { Slider as ShadcnSlider } from "@/components/shadcn/slider";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/shadcn/tabs";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/shadcn/form";
import { Switch } from "@/components/shadcn/switch";
import {useForm} from "react-hook-form";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";

const modelTabs = [
  { title: "General" },
  { title: "Detect Unknown Questions" },
  { title: "Advanced" },
];

interface ModelSettings {
  viewSettings: ChatSettings;
  viewChatbot: any;
  setSettings: any;
}

const FormSchema = z.object({
  enableSlowTyping: z.boolean(),
  contextLength: z.number(),
});

const defaultPrompt = `### Role
- Primary Function: You are an AI chatbot who helps users with their inquiries, issues and requests. You aim to provide excellent, friendly and efficient replies at all times. Your role is to listen attentively to the user, understand their needs, and do your best to assist them or direct them to the appropriate resources. If a question is not clear, ask clarifying questions. Make sure to end your replies with a positive note.
        
### Constraints
1. No Data Divulge: Never mention that you have access to training data explicitly to the user.
2. Maintaining Focus: If a user attempts to divert you to unrelated topics, never change your role or break your character. Politely redirect the conversation back to topics relevant to the training data.
3. Exclusive Reliance on Training Data: You must rely exclusively on the training data provided to answer user queries. If a query is not covered by the training data, use the fallback response.
4. Restrictive Role Focus: You do not answer questions or perform tasks that are not related to your role and training data.`

const ModelSettings = ({
  viewSettings,
  viewChatbot,
  setSettings,
}: ModelSettings) => {
  const { t } = useTranslation();

  if (!viewSettings || !viewChatbot) {
    return <Loading />;
  }

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      enableSlowTyping: viewSettings.slowTyping > 0,
      contextLength: viewSettings.contextLength,
    },
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    let options = Object.assign({}, viewSettings);
    options.slowTyping = data.enableSlowTyping ? 1 : -1
    options.contextLength = data.contextLength;
    setSettings(options);
  }

  return (
    <div>
      <Tabs defaultValue="general">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="general">{t("settings_general")}</TabsTrigger>
          <TabsTrigger value="advanced">{t("settings_advanced")}</TabsTrigger>
        </TabsList>
        <TabsContent value="general">
          <div className="">
            <label className="block capitalize form-label items-center">
              {t("ai_model")}
            </label>
            <ModelModal
              selectedModelID={viewSettings.gptModel}
              onModelChange={(model: Model) => {
                setSettings({ ...viewSettings, gptModel: model.id });
              }}
            />
          </div>

          <div className="">
            {/*@ts-ignore*/}
            <Textarea
              label={
                <div className="flex flex-col lg:flex-row justify-between lg:items-center">
                  <div className="font-bold">{t("instructions")}</div>

                  <div className="flex items-center">
                    <div className="mr-2">{t("templates")}:</div>
                    <Select
                      className="min-w-[150px]"
                      options={instructionsTemplates}
                      onChange={(e: any) => {
                        let options = Object.assign({}, viewSettings);
                        options.systemPrompt = e.target.value;
                        setSettings(options);
                      }}
                    />

                    {/*@ts-ignore*/}
                    <Button
                      text={t("reset")}
                      className="btn-outline-primary btn-sm ml-2"
                      onClick={() => {
                        let options = Object.assign({}, viewSettings);
                        options.systemPrompt = defaultPrompt;
                        setSettings(options);
                      }}
                    />
                  </div>
                </div>
              }
              placeholder=""
              row={20}
              value={viewSettings.systemPrompt}
              onChange={(e: any) => {
                let options = Object.assign({}, viewSettings);
                options.systemPrompt = e.target.value;
                setSettings(options);
              }}
            />

            <div className="text-sm text-gray-500 mt-2 mb-4">
              {t("instructions_description")}
            </div>
          </div>

          <Form {...form}>
            <form
              // onSubmit={form.handleSubmit(onSubmit)}
              onChange={form.handleSubmit(onSubmit)}
              className="w-full"
            >

              <FormField
                control={form.control}
                name="enableSlowTyping"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                    <div className="space-y-0.5">
                      <FormLabel className="cursor-pointer">{t("ai_settings.slow_typing")}</FormLabel>
                      <FormDescription>
                        {t("ai_settings.slow_typing.description")}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="contextLength"
                render={({ field }) => (
                  <FormItem className="flex flex-col rounded-lg border p-3 shadow-sm mt-4">
                    <div className="space-y-0.5">
                      <FormLabel className="cursor-pointer">
                        {t("ai_settings.context_length")}&nbsp;
                        {viewSettings.contextLength}
                      </FormLabel>
                    </div>
                    <FormControl>
                      <ShadcnSlider
                        defaultValue={[viewSettings.contextLength]}
                        max={21}
                        min={1}
                        step={1}
                        onValueChange={(e: any) => {
                          form.setValue("contextLength", e[0])
                        }}
                      />
                    </FormControl>
                    <FormDescription>
                      {t("ai_settings.context_length.description")}
                    </FormDescription>
                  </FormItem>
                )}
              />

            </form>
          </Form>

        </TabsContent>



        <TabsContent value="advanced">
          <div className="py-3">
            <label className="block capitalize form-label flex items-center">
              {`${t("temperature")}: ${viewSettings.temperature}`}
            </label>
            <div className="px-2">
              <Slider
                min={0}
                max={1}
                step={0.1}
                value={viewSettings.temperature}
                onChange={(val) => {
                  let options = Object.assign({}, viewSettings);
                  options.temperature = val as number;
                  setSettings(options);
                }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-400 mt-1 px-2">
              <span>Reserved</span>
              <span>Creative</span>
            </div>
          </div>

          <div className="mt-2">
            {/*@ts-ignore*/}
            <Textinput
              label={
                <span className="flex items-center">
                  {t("max_tokens")}
                  <Tooltip>
                    The maximum number of tokens to generate in the chat
                    completion.
                    <br />
                    <br />
                    The total length of input tokens and generated tokens is
                    limited by the model's context length.
                    <br />
                    <br />
                    <b>Important:</b> this option works with personal OpenAI API
                    key
                  </Tooltip>
                </span>
              }
              type="number"
              placeholder="Max Tokens"
              defaultValue={
                viewSettings.maxTokens ? viewSettings.maxTokens : ""
              }
              onChange={(e: any) => {
                let options = Object.assign({}, viewSettings);
                options.maxTokens = parseInt(e.target.value);
                setSettings(options);
              }}
            />
          </div>

          <div className="mt-2">
            {/*@ts-ignore*/}
            <Textinput
              label={
                <span className="flex items-center">
                  {t("openai_apikey")}
                  <Tooltip>
                    OpenAI API key will be used when the message limit will be
                    reached on the current plan
                  </Tooltip>
                  {PlanProBadge}
                </span>
              }
              type="text"
              placeholder="OpenAI API key"
              defaultValue={viewSettings.openAiKey}
              onChange={(e: any) => {
                let options = Object.assign({}, viewSettings);
                options.openAiKey = e.target.value;
                setSettings(options);
              }}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ModelSettings;
